import BpkPagination from '@skyscanner/backpack-web/bpk-component-pagination';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import FiltersAndSortSelectors from '../../FiltersAndSortSelectors';
import ReviewImages from '../../ReviewImages';
import ReviewProviders from '../../ReviewProviders';
import ViewMoreReviewsLink from '../../ViewMoreReviewsLink';
import ReviewCardList from '../ExpandableReviewsCustomizeMobile/ReviewsCustomizeMobile/InfiniteScrollReviews/ReviewCardsList/ReviewCardList';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './ReviewsCustomizeDesktop.module.scss';

import {
  useReviews,
  useTotalReviewCount,
  useTotalPage,
  useHotelReviewsContext,
  usePageNumber,
  useTaReview,
} from '@hotels-hotel-detail/data-access/hooks/useHotelReviewsContext';
import { buildAdditionalInfoPaginationClicked } from '@hotels-hotel-detail/utils/mini-events/hotels-action';
import { REVIEWS_PER_PAGE_DESKTOP } from '@hotels-hotel-detail/utils/review-service';
import CenteredSpinner from '@shared/ui/components/CenteredSpinner';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { ACTION_TYPE } from '@shared/utils-frontend/mini-events/constants';
import { scrollTo } from '@shared/utils-frontend/navigation-service';

const cls = cssModules(STYLES);

const ReviewsCustomizeDesktop = ({
  i18n: { translate },
  miniEventTracker,
}: {
  i18n: I18nService;
  miniEventTracker: MiniEventTracker;
}) => {
  const totalReviewCount = useTotalReviewCount();
  const reviews = useReviews();
  const totalPage = useTotalPage();
  const pageNumber = usePageNumber();
  const { fetchOffset, isLoading } = useHotelReviewsContext();
  const taReview = useTaReview();
  const shouldShowTaLink = pageNumber + 1 === totalPage && !!taReview;

  const onPageChange = (index: number) => {
    const anchorElement = document.getElementById('hotel-reviews-content');
    if (anchorElement) {
      scrollTo(anchorElement, 'instant');
    }
    fetchOffset(index * REVIEWS_PER_PAGE_DESKTOP);
    miniEventTracker.trackHotelsAction(
      ACTION_TYPE.DETAILS_REVIEW_PAGINATION_CLICK,
      buildAdditionalInfoPaginationClicked({ pageNum: index }),
    );
  };

  return (
    <div id="hotel-reviews-content">
      {totalReviewCount > 0 && (
        <div className={cls('ReviewsCustomizeDesktop')}>
          <ReviewProviders height={30} />
          <ReviewImages />
          <FiltersAndSortSelectors />
          {isLoading ? (
            <CenteredSpinner
              className={cls('ReviewsCustomizeDesktop__spinner')}
            />
          ) : (
            <>
              <ReviewCardList elements={reviews} />
              {shouldShowTaLink && <ViewMoreReviewsLink />}
              {totalPage > 1 && (
                <div className={cls('ReviewsCustomizeDesktop__pagination')}>
                  <BpkPagination
                    pageCount={totalPage}
                    selectedPageIndex={pageNumber}
                    onPageChange={onPageChange}
                    disabled={isLoading}
                    paginationLabel={`page ${pageNumber + 1}`}
                    previousLabel={translate('aria_btn_previous')}
                    nextLabel={translate('aria_btn_next')}
                    visibleRange={REVIEWS_PER_PAGE_DESKTOP}
                    pageLabel={(index: number) => `${index + 1}`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default withMiniEventTracker(withI18n(ReviewsCustomizeDesktop));
